import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Button, CardHeader, Col, Form, Modal, Row } from "react-bootstrap";
import { Card, CardBody, Input, Label } from "reactstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  faFloppyDisk,
  faMapMarkerAlt,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useGetOwnerSearchResultsMutation,
  useUploadRestaurantLogoFileExtntionHttpsMutation,
} from "../../../redux/services/restaurantSearchService";
import { uploadImage } from "../../../components/imageUploadCompont";
import {
  useCreateRestaurantHttpsMutation,
  useGetRestaurantCategoriesQuery,
  useGetRestaurantFlowTypesQuery,
  useGetRestaurantPropertiesQuery,
} from "../../../redux/services/restaurantServices";
import {
  capitalizeFirstLetter,
  categoryTagsOptionsFunction,
  getImageUrl,
} from "../../../utility/Utility";
import BreadCrumbs from "../../../components/BreadCrumbs";
import IMGUploadComponent from "../../../components/IMGUploadComponent";
import ControlledRowTextField from "../../../components/ControlledRowTextField";
import ControlledRowTextFieldAddress from "../../../components/ControlledRowTextFieldAddress";
import ControlledRowTextFieldTime from "../../../components/ControlledRowTextFieldTime";
import ControlledSelect from "../../../components/ControlledSelect";
import ControlledRowTextFieldDropDown from "../../../components/ControlledTextFieldDropDown";
import Map from "../../../components/LocationSelectorModal";
import { UpdateImageDetails } from "../../../components/MenuItemImageUpdate/MenuItemImageUpdate";
import "./index.css";
import LoadingMessage from "../../../components/LoadingMessage";
import ButtonComponentV2 from "../../../components/ButtonComponentV2";

type Location = {
  latitude: any;
  longitude: any;
};

export type RestaurantFormData = {
  searchRest: string;
  address: string;
  displayName: string;
  categoryTags: any;
  imageUrl: string | "";
  location: Location;
  ownerIdBySuperAdmin: string;
  openingTime: any;
  closingTime: any;
  type: string;
  imageReference: string | "";
  latitude: any;
  longitude: any;
  restaurantProperties?: any;
  supportsDirectOrders?: boolean;
  canCollectMoney?: boolean;
  flowTypes?: any;
  acceptCartReturns: boolean;
  deliverySettings: {
    isFreeDelivery: boolean;
    perItemDeliveryFee: number;
  };
};

export type LogoFileExtntionRequest = {
  fileExtention: string;
};
export type UpdateImageRequest = {
  restaurantId: string;
  menuItemId: string;
  newImages: Array<UpdateImageDetails>;
};

export type LogoFileExtntionResponse = {
  result: {
    imageReference: string;
    imageUploadUrl: string;
  };
};

export interface OwnerData {
  value: string;
  restaurantId: number;
  restaurantName: string;
}

const AddRestaurant = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [imageError, setImageError] = useState("");
  const [isDirectOrders, setIsDirectOrders] = useState(false);
  const [isCollectMoney, setIsCollectMoney] = useState(false);
  const [isCanAcceptReturnRequests, setIsCanAcceptReturnRequests] =
    useState(false);
  const [searchOptions, setSearchOptions] = useState<OwnerData[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isSearching, setIsSearching] = useState(false);
  const [selectedOwnerId, setSelectedOwnerId] = useState("");
  const [selectedUserName, setSelectedUserName] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchTextComform, setSearchTextComform] = useState(false);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isFreeDelivery, setIsFreeDelivery] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [perItemDeliveryFee, setPerItemDeliveryFee] = useState(0);

  const [uploadedImgDetails, setUploadedImgDetails] = useState<{
    imgSrc: string;
    formData: any;
    fileExtention: string;
  }>({
    imgSrc: "",
    formData: "",
    fileExtention: "",
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedLocation, setSelectedLocation] = useState({
    lat: 28.7041,
    lng: 77.1025,
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RestaurantFormData>({
    defaultValues: {
      searchRest: "",
      address: "",
      displayName: "",
      categoryTags: [],
      imageUrl: "",
      location: { latitude: 0, longitude: 0 },
      ownerIdBySuperAdmin: selectedOwnerId,
      openingTime: 0,
      closingTime: 0,
      type: "",
      imageReference: "",
      latitude: 0,
      longitude: 0,
      restaurantProperties: [],
      supportsDirectOrders: false,
      canCollectMoney: false,
      flowTypes: [],
      acceptCartReturns: false,
      deliverySettings: {
        isFreeDelivery: false,
        perItemDeliveryFee: 0,
      },
    },
  });

  const searchRestWatcher = watch("searchRest");

  let selectedRestaurantId = localStorage.getItem("selectedRestaurantId") || "";

  const [createRestaurant] = useCreateRestaurantHttpsMutation();
  const [uploadLogoFileExtetion] =
    useUploadRestaurantLogoFileExtntionHttpsMutation();

  const createRestaurantMutation = async (requestData: RestaurantFormData) => {
    try {
      setIsLoading(true);
      const imageUrl = await uploadImage(uploadLogoFileExtetion, {
        fileExtention: uploadedImgDetails.fileExtention,
        formData: uploadedImgDetails.formData,
      });
      const restaurantResponse: any = await createRestaurant({
        ...requestData,
        imageReference: imageUrl,
      });
      if ("data" in restaurantResponse) {
        if (selectedRestaurantId === "") {
          localStorage.setItem(
            "selectedRestaurantId",
            restaurantResponse?.data?.id || ""
          );
        }
        toast.success("Restaurant Created");
        navigate("/restaurants/restaurants-list");
      } else if (restaurantResponse?.error?.status === 409) {
        toast.error(
          "You cannot create two restaurants from same name in same place"
        );
      }
    } catch (error: any) {
      if (error.status === 409) {
        toast.error(
          "You cannot create two restaurants from same name in same place"
        );
      }
      toast.error("Failed to create restaurant. please check Enter Values");
    } finally {
      setIsLoading(false);
    }
  };

  const imageUploadCallback = (e: any) => {
    setUploadedImgDetails({
      ...uploadedImgDetails,
      imgSrc: e.imgSrc,
      formData: e.formData,
      fileExtention: e.fileExtention,
    });
    setImageError("");
  };

  //Category Tags

  const { data, isSuccess } = useGetRestaurantCategoriesQuery("");
  let categoryTagsOptions = [{ value: "", label: "" }];
  if (isSuccess) {
    categoryTagsOptions = categoryTagsOptionsFunction(data) || [
      { value: "", label: "" },
    ];
  }

  //Restaurant Properties

  const restaurantPropertiesFunction = (data: any) => {
    if (data && data.RestaurantPropertiesList) {
      const restauarntPropertiesArray = data.RestaurantPropertiesList;
      return restauarntPropertiesArray.map((item: any) => {
        return {
          value: capitalizeFirstLetter(item.displayName),
          label: (
            <div style={{ display: "flex", justifyContent: "left", gap: "2%" }}>
              <img
                src={getImageUrl(item.displayName)}
                alt={item.displayName}
                width="20px"
                height="20px"
              />
              {item.displayName}
            </div>
          ),
        };
      });
    } else {
      console.error("Invalid data structure:", data);
      return [];
    }
  };

  const { data: restaurantProperties } = useGetRestaurantPropertiesQuery("");
  let restaurantPropertiesOptions = [{ value: "", label: "" }];
  if (isSuccess) {
    restaurantPropertiesOptions = restaurantPropertiesFunction(
      restaurantProperties
    ) || [{ value: "", label: "" }];
  }

  const restaurantFlowTypesFunction = (data: any) => {
    if (data && data.FlowTypesList) {
      const restaurantFlowTypessArray = data.FlowTypesList;
      return restaurantFlowTypessArray.map((item: any) => {
        return {
          value: item.displayName,
          label: (
            <div style={{ display: "flex", justifyContent: "left", gap: "2%" }}>
              {item.displayName}
            </div>
          ),
        };
      });
    } else {
      console.error("Invalid data structure:", data);
      return [];
    }
  };

  const { data: restaurantFlowTypes } = useGetRestaurantFlowTypesQuery("");
  let restaurantFlowTypesOptions = [{ value: "", label: "" }];
  if (isSuccess) {
    restaurantFlowTypesOptions = restaurantFlowTypesFunction(
      restaurantFlowTypes
    ) || [{ value: "", label: "" }];
  }

  //Form Submit
  const onSubmit = async (data: RestaurantFormData) => {
    if (!uploadedImgDetails.formData) {
      setImageError("Please upload an image.");
      return;
    }
    const latitude = parseFloat(data.latitude);
    const longitude = parseFloat(data.longitude);
    const categroryTagList = data?.categoryTags.map((item: any) => item.value);
    const restaurantProperties = data?.restaurantProperties.map(
      (item: any) => item.value
    );
    const flowTypes = data?.flowTypes.map((item: any) => item.value);
    const requestData = {
      ...data,
      openingTime: data.openingTime || "08:30",
      closingTime: data.closingTime || "20:30",
      location: {
        latitude: latitude,
        longitude: longitude,
      },
      categoryTags: categroryTagList,
      restaurantProperties: restaurantProperties,
      selectedOwnertId: selectedOwnerId,
      ownerIdBySuperAdmin: selectedOwnerId,
      supportsDirectOrders: data.supportsDirectOrders,
      canCollectMoney: data.canCollectMoney,
      flowTypes: flowTypes,
      acceptCartReturns: data.acceptCartReturns,
      deliverySettings: {
        isFreeDelivery: data.deliverySettings.isFreeDelivery,
        perItemDeliveryFee:
          parseFloat(data.deliverySettings.perItemDeliveryFee.toString()) || 0,
      },
    };
    createRestaurantMutation(requestData);
  };

  const handleLocationModalClose = () => {
    setShowModal(false);
  };

  const handleLocationSave = (
    latitude: number,
    longitude: number,
    address: string
  ) => {
    setSelectedLocation({ lat: latitude, lng: longitude });
    setValue("latitude", latitude);
    setValue("longitude", longitude);
    setValue("address", address);
  };
  const [triggerRestaurantSearchList] = useGetOwnerSearchResultsMutation();

  const handleSearch = async () => {
    setIsSearching(true);
    try {
      const obj = {
        params: `query=${searchRestWatcher}`,
        filters: "userType:Admin",
      };

      const response: any = await triggerRestaurantSearchList(obj);

      if (response?.data) {
        const options = response.data.hits.map((item: any) => ({
          value: item.documentId,
          label: item.displayName,
          email: item.email,
        }));
        setSearchOptions(options);
      } else if (response?.errors) {
        toast.error("Something went wrong...! Please try again.");
      }
    } catch (error) {
      console.error("This is the error", error);
    }
    setIsSearching(false);
  };

  const handaleSearchTextComform = () => {
    if (typingTimer.current) clearTimeout(typingTimer.current);
    setSearchTextComform(false);

    if (searchRestWatcher.length) {
      typingTimer.current = setTimeout(() => {
        setSearchTextComform(true);
      }, 3000);
    }

    return () => {
      if (typingTimer.current) {
        clearTimeout(typingTimer.current);
      }
    };
  };

  useEffect(() => {
    if (searchRestWatcher.length > 3) {
      handaleSearchTextComform();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchRestWatcher]);

  useEffect(() => {
    if (searchTextComform) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTextComform]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as HTMLElement).closest(".autocomplete")) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <br />
      <Card className="p-3">
        <CardHeader className="card-header">
          <div className="d-flex justify-content-between p-2">
            <h5 className="">Add Restaurant</h5>
          </div>
          <hr />
        </CardHeader>
        <CardBody>
          <Form
            className="auth-login-form mt-2"
            onSubmit={handleSubmit(onSubmit)}
          >
            {!isSuccess ? (
              <div className="bg-white">
                <LoadingMessage />
              </div>
            ) : (
              <>
                <IMGUploadComponent
                  imageUrl={uploadedImgDetails.imgSrc}
                  onUpload={imageUploadCallback}
                />
                <Row>
                  <Col lg="4">{""}</Col>
                  <Col lg="8">
                    {imageError && (
                      <p className="text-danger ml-4 text-align-right">
                        {imageError}
                      </p>
                    )}
                  </Col>
                </Row>
                <hr />
                <Row className="mb-3">
                  <Col lg="4">
                    <Label className="form-label">Owner Name</Label>
                  </Col>
                  <Col lg="8">
                    <div className="autocomplete">
                      <Controller
                        name="searchRest"
                        control={control}
                        rules={{ required: "Owner name is required." }}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <Input
                              type="text"
                              name="searchRest"
                              placeholder="Enter Owner Name"
                              autoComplete="off"
                              onChange={(e) => {
                                setSelectedUserName(e.target.value);
                                field.onChange(e);
                                if (searchRestWatcher.length < 3) {
                                  setSearchOptions([]);
                                  setShowSuggestions(false);
                                } else {
                                  setShowSuggestions(true);
                                }
                              }}
                              control={control}
                              value={selectedUserName}
                              rules={{ required: "Owner name is required." }}
                              errors={errors}
                            />
                            {error && (
                              <span className="validation-message text-danger">
                                {error.message}
                              </span>
                            )}
                          </>
                        )}
                      />

                      {showSuggestions && searchOptions.length > 0 && (
                        <div className="autocomplete-suggestions">
                          {searchOptions.map((option: any) => (
                            <div
                              key={option.value}
                              className="autocomplete-suggestion"
                              onClick={() => {
                                setSelectedOwnerId(option.value);
                                setSelectedUserName(option.label);
                                setValue("ownerIdBySuperAdmin", option.value);
                                setShowSuggestions(false);
                              }}
                            >
                              <div className="ownerName-label">
                                {option.label}
                              </div>
                              <span className="ownerName-email">
                                {option.email}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
                <hr />
                <ControlledRowTextField
                  label={"Name"}
                  inputName={"displayName"}
                  autoComplete={"off"}
                  inputType={"text"}
                  placeholder={"Enter Restaurant Name"}
                  control={control}
                  rules={{ required: "Restaurant name is required." }}
                  errors={errors}
                />
                <Row>
                  <Col lg="4">{""}</Col>
                  <Col lg="8">
                    {errors.displayName && (
                      <span className="validation-message text-danger">
                        {errors.displayName.message}
                      </span>
                    )}
                  </Col>
                </Row>
                <hr />
                <ControlledRowTextFieldAddress
                  label={"Address"}
                  inputName={"address"}
                  inputType={"text"}
                  placeholder={"Select Address From Location Button"}
                  isRequired={true}
                  control={control}
                  setShowModal={setShowModal}
                  rules={{ required: "Please enter an address" }}
                  errors={errors}
                />
                <hr />
                <Row style={{ display: "none" }}>
                  <Col lg="5">
                    <ControlledRowTextField
                      label={"Latitude"}
                      inputName={"latitude"}
                      inputType={"number"}
                      placeholder={"Enter Latitude"}
                      isRequired={true}
                      control={control}
                    />
                  </Col>
                  <Col lg="5">
                    <ControlledRowTextField
                      label={"Longitude"}
                      inputName={"longitude"}
                      inputType={"number"}
                      placeholder={"Enter Longitude"}
                      isRequired={true}
                      control={control}
                    />
                  </Col>
                  <Col lg="2">
                    <Button
                      variant="info"
                      type="button"
                      onClick={() => setShowModal(true)}
                    >
                      <FontAwesomeIcon icon={faMapMarkerAlt} />
                    </Button>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Label>Restaurant Available Time</Label>
                  </Col>
                  <Col>
                    <ControlledRowTextFieldTime
                      label={"Opening Time"}
                      inputName={"openingTime"}
                      control={control}
                      isRequired={true}
                      inputType={"time"}
                      defaultValue={"08:30"}
                    />
                  </Col>
                  <Col>
                    <ControlledRowTextFieldTime
                      label={"Closing Time"}
                      inputName={"closingTime"}
                      control={control}
                      isRequired={true}
                      inputType={"time"}
                      defaultValue={"20:30"}
                    />
                  </Col>
                </Row>
                <hr />
                <ControlledSelect
                  label={"Categories"}
                  inputName={"categoryTags"}
                  control={control}
                  isMulti={true}
                  optionsList={categoryTagsOptions}
                  rules={{ required: "Please select restaurant categories." }}
                  errors={errors}
                  placeholder="Select Restaurant Categories"
                  isClear={true}
                />
                <hr />
                <ControlledSelect
                  label={"Properties"}
                  inputName={"restaurantProperties"}
                  control={control}
                  isMulti={true}
                  optionsList={restaurantPropertiesOptions}
                  rules={{ required: "Please select restaurant properties." }}
                  errors={errors}
                  placeholder="Select Restaurant properties"
                  isClear={true}
                />
                <hr />
                <ControlledSelect
                  label={"Flow Types"}
                  inputName={"flowTypes"}
                  control={control}
                  isMulti={true}
                  optionsList={restaurantFlowTypesOptions}
                  isRequired={true}
                />
                <hr />
                <ControlledRowTextFieldDropDown
                  label={"Type"}
                  inputName={"type"}
                  inputType={"dropdown"}
                  placeholder={"Select Restaurant Type"}
                  isRequired={true}
                  control={control}
                  rules={{ required: "Please select a restaurant type" }}
                  errors={errors}
                />
                <hr />
                <Row>
                  <Col lg="4">
                    <Label
                      className="form-check-label"
                      htmlFor="supportsDirectOrders"
                    >
                      Can Direct Pickup
                    </Label>
                  </Col>
                  <Col lg="8">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="supportsDirectOrders"
                      checked={isDirectOrders}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setIsDirectOrders(event.target.checked);
                        setValue("supportsDirectOrders", event.target.checked);
                      }}
                    />
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col lg="4">
                    <Label
                      className="form-check-label"
                      htmlFor="supportsDirectOrders"
                    >
                      Can Collect Money
                    </Label>
                  </Col>
                  <Col lg="8">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="canCollectMoney"
                      checked={isCollectMoney}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setIsCollectMoney(event.target.checked);
                        setValue("canCollectMoney", event.target.checked);
                      }}
                    />
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col lg="4">
                    <Label
                      className="form-check-label"
                      htmlFor="canCollectMoney"
                    >
                      Accecpt Cart Return Requests
                    </Label>
                  </Col>
                  <Col lg="8">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="acceptCartReturns"
                      checked={isCanAcceptReturnRequests}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setIsCanAcceptReturnRequests(event.target.checked);
                        setValue("acceptCartReturns", event.target.checked);
                      }}
                    />
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col lg="4">
                    <Label
                      className="form-check-label"
                      htmlFor="isFreeDelivery"
                    >
                      Free Delivery
                    </Label>
                  </Col>
                  <Col lg="8">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="isFreeDelivery"
                      required={true}
                      checked={isFreeDelivery}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setIsFreeDelivery(event.target.checked);
                        setValue(
                          "deliverySettings.isFreeDelivery",
                          event.target.checked
                        );
                      }}
                    />
                  </Col>
                </Row>
                <hr />

                <ControlledRowTextField
                  label={"Per Item Delivery Fee"}
                  inputName={"deliverySettings.perItemDeliveryFee"}
                  inputType={"number"}
                  placeholder={"Enter delivery fee"}
                  control={control}
                  isRequired={true}
                  rules={{
                    required:
                      "Delivery fee is required when free delivery is disabled",
                    min: {
                      message: "Delivery fee cannot be negative",
                    },
                  }}
                  errors={errors}
                />

                <br />
                <div style={{ textAlign: "end" }}>
                  {isLoading ? (
                    <ButtonComponentV2
                      type={"submit"}
                      icon={
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          title="Saving..."
                        />
                      }
                      titel="Saving..."
                      isDisabled={true}
                      backgroundColor="#590B21"
                    />
                  ) : (
                    <ButtonComponentV2
                      type="submit"
                      icon={<FontAwesomeIcon icon={faFloppyDisk} />}
                      titel="Save"
                      isDisabled={isLoading}
                      backgroundColor="#590B21"
                    />
                  )}
                </div>
              </>
            )}
            <br />
            <Modal
              show={showModal}
              onHide={() => setShowModal(false)}
              centered
              size="lg"
              backdrop="static"
              keyboard={false}
            >
              <Modal.Header closeButton>
                <Modal.Title>Select your location</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Map onSaveLocation={handleLocationSave} />
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => handleLocationModalClose()}
                >
                  Save
                </Button>
              </Modal.Footer>
            </Modal>
          </Form>
        </CardBody>
      </Card>
    </>
  );
};

export default AddRestaurant;
